/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

/*
* When adding modules to the namespace, ensure that they are added in alphabetical order according to module name.
*/

// MODULES //

import setReadOnly from '@stdlib/utils/define-read-only-property';


// MAIN //

/**
* Top-level namespace.
*
* @namespace cosine
*/
var cosine = {};

/**
* @name cdf
* @memberof cosine
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/cosine/cdf}
*/
import cdf from './../../../../base/dists/cosine/cdf';
setReadOnly( cosine, 'cdf', cdf );

/**
* @name Cosine
* @memberof cosine
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/cosine/ctor}
*/
import Cosine from './../../../../base/dists/cosine/ctor';
setReadOnly( cosine, 'Cosine', Cosine );

/**
* @name kurtosis
* @memberof cosine
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/cosine/kurtosis}
*/
import kurtosis from './../../../../base/dists/cosine/kurtosis';
setReadOnly( cosine, 'kurtosis', kurtosis );

/**
* @name logcdf
* @memberof cosine
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/cosine/logcdf}
*/
import logcdf from './../../../../base/dists/cosine/logcdf';
setReadOnly( cosine, 'logcdf', logcdf );

/**
* @name logpdf
* @memberof cosine
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/cosine/logpdf}
*/
import logpdf from './../../../../base/dists/cosine/logpdf';
setReadOnly( cosine, 'logpdf', logpdf );

/**
* @name mean
* @memberof cosine
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/cosine/mean}
*/
import mean from './../../../../base/dists/cosine/mean';
setReadOnly( cosine, 'mean', mean );

/**
* @name median
* @memberof cosine
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/cosine/median}
*/
import median from './../../../../base/dists/cosine/median';
setReadOnly( cosine, 'median', median );

/**
* @name mgf
* @memberof cosine
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/cosine/mgf}
*/
import mgf from './../../../../base/dists/cosine/mgf';
setReadOnly( cosine, 'mgf', mgf );

/**
* @name mode
* @memberof cosine
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/cosine/mode}
*/
import mode from './../../../../base/dists/cosine/mode';
setReadOnly( cosine, 'mode', mode );

/**
* @name pdf
* @memberof cosine
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/cosine/pdf}
*/
import pdf from './../../../../base/dists/cosine/pdf';
setReadOnly( cosine, 'pdf', pdf );

/**
* @name quantile
* @memberof cosine
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/cosine/quantile}
*/
import quantile from './../../../../base/dists/cosine/quantile';
setReadOnly( cosine, 'quantile', quantile );

/**
* @name skewness
* @memberof cosine
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/cosine/skewness}
*/
import skewness from './../../../../base/dists/cosine/skewness';
setReadOnly( cosine, 'skewness', skewness );

/**
* @name stdev
* @memberof cosine
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/cosine/stdev}
*/
import stdev from './../../../../base/dists/cosine/stdev';
setReadOnly( cosine, 'stdev', stdev );

/**
* @name variance
* @memberof cosine
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/cosine/variance}
*/
import variance from './../../../../base/dists/cosine/variance';
setReadOnly( cosine, 'variance', variance );


// EXPORTS //

export default cosine;
